import { useEffect, useMemo, useRef, useState } from 'react';
import { FACTIONS } from '../../content/factions';
import { orderDeckByAscension } from '../../content/deckOrder';
import { defs } from '../../content/registry';
import type { CardCategory, CardDef, CardRarity, FactionId } from '../../content/types';
import {
  DECK_SIZE,
  deleteDeck,
  loadDecks,
  loadSettings,
  newDeckId,
  saveDeck,
  sanitizeDeckCards,
  type SavedDeck,
} from '../../app/persistence';
import { CardFace, CardInspector, rarityOf } from '../cards';
import { DECK_HOTKEYS } from '../hud';
import { Select } from '../components/Select';
import { useConfirm } from '../components/Modal';

const FACTION_IDS = Object.keys(FACTIONS) as FactionId[];
type SortMode = 'ascension' | 'name' | 'cost' | 'rarity';
const RARITY_ORDER: Record<CardRarity, number> = { common: 0, uncommon: 1, rare: 2, legendary: 3 };
const CARDS_PER_PAGE = 8;
// The native <select> showed raw enum values ("unit", "common"); the styled Select shows prose.
const CATEGORY_LABELS: Record<CardCategory, string> = {
  unit: 'Units',
  building: 'Buildings',
  spell: 'Spells',
  trap: 'Traps',
  ritual: 'Rituals',
  boss: 'Bosses',
};
const RARITY_LABELS: Record<CardRarity, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  legendary: 'Legendary',
};
const ROMAN_TIERS = ['I', 'II', 'III', 'IV'] as const;

function factionPool(faction: FactionId): CardDef[] {
  return [...defs.cards.values()].filter((card) => card.faction === faction && card.deckEligible !== false);
}

export function DeckEditor({ onBack }: { onBack: () => void }) {
  const [confirm, confirmDialog] = useConfirm();
  const visualSettings = useMemo(() => loadSettings(), []);
  const [faction, setFaction] = useState<FactionId>('iron-covenant');
  const [cards, setCards] = useState<string[]>(() => orderDeckByAscension(FACTIONS['iron-covenant'].defaultDeck));
  const [deckName, setDeckName] = useState('New Deck');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>(() => factionPool('iron-covenant')[0]?.id ?? '');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<'all' | CardCategory>('all');
  const [ascension, setAscension] = useState<'all' | '1' | '2' | '3' | '4'>('all');
  const [rarity, setRarity] = useState<'all' | CardRarity>('all');
  const [sort, setSort] = useState<SortMode>('ascension');
  const [page, setPage] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [savedTick, setSavedTick] = useState(0);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const undoRef = useRef<string[][]>([]);
  const redoRef = useRef<string[][]>([]);

  const allCards = useMemo(() => factionPool(faction), [faction]);
  const pool = useMemo(() => {
    const text = query.trim().toLowerCase();
    return allCards
      .filter((card) => category === 'all' || card.category === category)
      .filter((card) => ascension === 'all' || card.ascension === Number(ascension))
      .filter((card) => rarity === 'all' || rarityOf(card) === rarity)
      .filter((card) => !text || `${card.name} ${card.ui.roleLine} ${card.ui.rulesText}`.toLowerCase().includes(text))
      .sort((a, b) => {
        if (sort === 'name') return a.name.localeCompare(b.name);
        if (sort === 'cost') return a.cost.power - b.cost.power || a.cost.essence - b.cost.essence;
        if (sort === 'rarity') return RARITY_ORDER[rarityOf(b)] - RARITY_ORDER[rarityOf(a)] || a.name.localeCompare(b.name);
        return a.ascension - b.ascension || a.cost.power - b.cost.power;
      });
  }, [allCards, ascension, category, query, rarity, sort]);
  const pageCount = Math.max(1, Math.ceil(pool.length / CARDS_PER_PAGE));
  const visiblePage = Math.min(page, pageCount - 1);
  const pageCards = pool.slice(visiblePage * CARDS_PER_PAGE, (visiblePage + 1) * CARDS_PER_PAGE);
  useEffect(() => setPage(0), [faction, query, category, ascension, rarity, sort]);
  const savedDecks = useMemo(() => loadDecks(faction), [faction, savedTick]);

  const setDeck = (next: string[], remember = true) => {
    if (remember) {
      undoRef.current.push(cards);
      redoRef.current = [];
    }
    setCards(orderDeckByAscension(next));
    setDirty(true);
  };
  const addCard = (cardId: string) => {
    if (cards.includes(cardId) || cards.length >= DECK_SIZE) return;
    setDeck([...cards, cardId]);
  };
  const removeCard = (cardId: string) => setDeck(cards.filter((id) => id !== cardId));
  const move = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= cards.length || to >= cards.length) return;
    const next = [...cards];
    const [card] = next.splice(from, 1);
    next.splice(to, 0, card);
    setDeck(next);
  };
  const undo = () => {
    const previous = undoRef.current.pop();
    if (!previous) return;
    redoRef.current.push(cards);
    setCards(previous);
    setDirty(true);
  };
  const redo = () => {
    const next = redoRef.current.pop();
    if (!next) return;
    undoRef.current.push(cards);
    setCards(next);
    setDirty(true);
  };

  // Was window.confirm — an unstyled browser dialog that reads as a page error inside a
  // full-screen game. useConfirm keeps the awaited-boolean ergonomics without blocking.
  const confirmDiscard = async () =>
    !dirty ||
    (await confirm({
      title: 'Discard unsaved changes?',
      body: 'This deck has edits that have not been saved.',
      confirmLabel: 'Discard',
      danger: true,
    }));
  const switchFaction = async (next: FactionId) => {
    if (next === faction || !(await confirmDiscard())) return;
    setFaction(next);
    const deck = orderDeckByAscension(FACTIONS[next].defaultDeck);
    setCards(deck);
    setDeckName('New Deck');
    setCurrentId(null);
    setSelected(factionPool(next)[0]?.id ?? '');
    setDirty(false);
    undoRef.current = [];
    redoRef.current = [];
  };
  const save = () => {
    const id = currentId ?? newDeckId();
    saveDeck({ id, name: deckName.trim() || 'Unnamed Deck', faction, cards });
    setCurrentId(id);
    setDirty(false);
    setSavedTick((value) => value + 1);
  };
  const load = async (deck: SavedDeck) => {
    if (!(await confirmDiscard())) return;
    setCards(sanitizeDeckCards(faction, deck.cards));
    setDeckName(deck.name);
    setCurrentId(deck.id);
    setDirty(false);
    undoRef.current = [];
    redoRef.current = [];
  };
  const duplicate = () => {
    const id = newDeckId();
    const name = `${deckName.trim() || 'Unnamed Deck'} (copy)`;
    saveDeck({ id, name, faction, cards });
    setCurrentId(id);
    setDeckName(name);
    setDirty(false);
    setSavedTick((value) => value + 1);
  };
  const removeDeck = async () => {
    if (!currentId) return;
    const ok = await confirm({
      title: `Delete “${deckName}”?`,
      body: 'This saved deck will be removed permanently.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    deleteDeck(currentId);
    setCurrentId(null);
    setDirty(false);
    setSavedTick((value) => value + 1);
  };

  const counts = cards.reduce<Record<string, number>>((out, cardId) => {
    const card = defs.cards.get(cardId);
    if (card) out[card.category] = (out[card.category] ?? 0) + 1;
    return out;
  }, {});
  const selectedCard = defs.cards.get(selected);
  const inDeck = selectedCard ? cards.includes(selectedCard.id) : false;

  return (
    <div
      className={`deckforge screen-in${visualSettings.reduceMotion ? ' reduce-motion' : ''}${visualSettings.largeCardText ? ' large-card-text' : ''}`}
      data-foil={visualSettings.foilEffects ? 'on' : 'off'}
    >
      <header className="deckforge-head">
        <div><span className="deckforge-kicker">THE FORGE</span><h1>Deck Editor</h1></div>
        <div className="deckforge-tabs">
          {FACTION_IDS.map((id) => <button key={id} className={id === faction ? 'active' : ''} onClick={() => void switchFaction(id)}>{FACTIONS[id].mark} {FACTIONS[id].name}</button>)}
        </div>
        <button className="btn" onClick={async () => { if (await confirmDiscard()) onBack(); }}>Back</button>
      </header>

      <div className="deckforge-toolbar">
        <input className="deckname" value={deckName} maxLength={40} onChange={(event) => { setDeckName(event.target.value); setDirty(true); }} />
        <Select
          ariaLabel="Saved decks"
          className="deckpicker"
          value={currentId ?? ''}
          placeholder="Saved decks…"
          options={savedDecks.map((deck) => ({ value: deck.id, label: deck.name, hint: `${deck.cards.length}/${DECK_SIZE}` }))}
          onChange={(id) => { const deck = savedDecks.find((item) => item.id === id); if (deck) void load(deck); }}
        />
        <span className={`deckdirty${dirty ? ' dirty' : ''}`}>{dirty ? 'Unsaved changes' : currentId ? 'Saved' : 'New deck'}</span>
        <button className="btn small" disabled={!undoRef.current.length} onClick={undo}>Undo</button>
        <button className="btn small" disabled={!redoRef.current.length} onClick={redo}>Redo</button>
        <button className="btn small" onClick={save}>Save</button>
        <button className="btn small" onClick={duplicate}>Duplicate</button>
        {currentId && <button className="btn small danger" onClick={() => void removeDeck()}>Delete</button>}
      </div>

      <main className="deckforge-main">
        <aside className="deckfilters">
          <label><span>Search</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, role, or rules…" /></label>
          <label><span>Category</span><Select
            ariaLabel="Filter by category"
            value={category}
            options={[{ value: 'all' as const, label: 'All categories' }, ...(['unit', 'building', 'spell', 'trap', 'ritual', 'boss'] as const).map((value) => ({ value, label: CATEGORY_LABELS[value] }))]}
            onChange={setCategory}
          /></label>
          <label><span>Ascension</span><Select
            ariaLabel="Filter by ascension tier"
            value={ascension}
            options={[{ value: 'all' as const, label: 'All tiers' }, ...(['1', '2', '3', '4'] as const).map((value) => ({ value, label: `Ascension ${ROMAN_TIERS[Number(value) - 1]}` }))]}
            onChange={setAscension}
          /></label>
          <label><span>Rarity</span><Select
            ariaLabel="Filter by rarity"
            value={rarity}
            options={[{ value: 'all' as const, label: 'All rarities' }, ...(['common', 'uncommon', 'rare', 'legendary'] as const).map((value) => ({ value, label: RARITY_LABELS[value] }))]}
            onChange={setRarity}
          /></label>
          <label><span>Sort</span><Select
            ariaLabel="Sort cards"
            value={sort}
            options={[
              { value: 'ascension' as const, label: 'Ascension' },
              { value: 'name' as const, label: 'Name' },
              { value: 'cost' as const, label: 'Power cost' },
              { value: 'rarity' as const, label: 'Rarity' },
            ]}
            onChange={setSort}
          /></label>
          <div className="decksummary"><b>{cards.length}/{DECK_SIZE}</b><span>⚔ {counts.unit ?? 0} units</span><span>🏰 {counts.building ?? 0} buildings</span><span>✨ {counts.spell ?? 0} spells</span><span>♛ {counts.boss ?? 0} bosses</span>{!counts.boss && <em>Bossless deck: legal, but harder to finish with.</em>}</div>
          <details><summary>In-match roster pieces</summary><p>Units trained from production buildings and Core annexes do not consume deck slots. Inspect a building card to see its roster.</p></details>
        </aside>

        <section className="deckcollection">
          <div className="decksection-title">
            <span>{pool.length} cards</span>
            <div className="deckpagination" aria-label="Card pages">
              <button type="button" onClick={() => setPage((value) => Math.max(0, value - 1))} disabled={visiblePage === 0} aria-label="Previous card page">‹</button>
              <b>{visiblePage + 1} / {pageCount}</b>
              <button type="button" onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))} disabled={visiblePage === pageCount - 1} aria-label="Next card page">›</button>
            </div>
            <small>Hover to inspect · click to add</small>
          </div>
          <div className="deckcardgrid">
            {pageCards.map((card) => <CardFace key={card.id} card={card} variant="pool" selected={selected === card.id} onClick={() => { setSelected(card.id); addCard(card.id); }} onHover={(hovered) => { if (hovered) setSelected(card.id); }} footer={cards.includes(card.id) ? '✓ in deck' : undefined} />)}
          </div>
        </section>

        <aside className="deckinspect">
          {selectedCard ? <>
            <CardInspector cardId={selectedCard.id} compact />
            <button className="deckinspect-action" disabled={!inDeck && cards.length >= DECK_SIZE} onClick={() => inDeck ? removeCard(selectedCard.id) : addCard(selectedCard.id)}>{inDeck ? 'Remove from deck' : cards.length >= DECK_SIZE ? 'Deck is full' : 'Add to deck'}</button>
          </> : <div className="emptyinspect">Select a card to inspect it.</div>}
        </aside>
      </main>

      <footer className="decktray">
        <div className="decktray-label"><b>{deckName || 'Unnamed Deck'}</b><span>Ascension I → IV · drag to reorder within a tier · slot order is the in-match hotkey order</span></div>
        <div className="decktray-slots">
          {Array.from({ length: DECK_SIZE }, (_, index) => {
            const cardId = cards[index];
            const card = cardId ? defs.cards.get(cardId) : undefined;
            return <div key={index} className={`deckslot${card ? ' filled' : ''}`} onDragOver={(event) => event.preventDefault()} onDrop={() => { if (dragIndex !== null && card) move(dragIndex, index); setDragIndex(null); }}>
              <span className="deckslot-key">{DECK_HOTKEYS[index]}</span>
              {card ? <div draggable onDragStart={() => setDragIndex(index)} onDragEnd={() => setDragIndex(null)}><CardFace card={card} variant="compact" onClick={() => setSelected(card.id)} /><span className="deckslot-tools"><button onClick={() => move(index, index - 1)} disabled={index === 0}>‹</button><button onClick={() => removeCard(card.id)}>×</button><button onClick={() => move(index, index + 1)} disabled={index === cards.length - 1}>›</button></span></div> : <span className="deckslot-empty">empty</span>}
            </div>;
          })}
        </div>
      </footer>
      {confirmDialog}
    </div>
  );
}
